import React, { useCallback, useContext, useMemo } from 'react';
import { Column } from 'react-table';
import { message, Button, Dropdown, Menu, Modal } from 'antd';
import * as Icons from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { Basket } from '../types';
import { BasketService } from '../services';
import { ParcelContext } from '@modules/parcels/contexts';
import { BasketTableContext } from '../contexts/table';
import { useBackgroundNavigate } from '@shared/hooks';
import { printDeclarationsWaybillUseCase } from '@modules/declarations/use-cases/print-waybill';
import { nextTableColumns } from '@shared/modules/next-table/helpers/next-table-columns';
import { localURLMaker } from '@shared/utils';
import { StopPropagation } from '@shared/components/stop-propagation';

export const useBasketsTableColumns = () => {
  const { handleFetch } = useContext(BasketTableContext);
  const navigate = useBackgroundNavigate();
  const { t } = useTranslation();
  const { currentParcel } = useContext(ParcelContext);

  const actions = useMemo<Column<Basket>>(
    () => ({
      ...nextTableColumns.actions,
      Cell: ({ row: { original } }) => {
        const onUpdate = useCallback(() => {
          navigate(localURLMaker('/baskets/update/:id', { id: original.id }, { name: original.name }), { withBackground: true });
        }, [original.id, original.name]);

        const onDelete = useCallback(() => {
          const action = async () => {
            await BasketService.remove([original.id]);
            handleFetch();
          };

          Modal.confirm({
            title: t('common.confirm'),
            content: t('baskets.confirm.delete'),
            onOk: action,
          });
        }, [original.id]);

        const toggleTransferModal = useCallback(() => {
          navigate('/baskets/declarations/' + original.id, { withBackground: true });
        }, [original.id])

        const addToContainer = useCallback(() => {
          const action = async () => {
            const result = await BasketService.addToContainer(original.id);
            if (result.status === 200) {
              message.success('Əməliyyat müvəffəqiyyətlə başa çatdı');
              printDeclarationsWaybillUseCase(undefined, undefined, currentParcel?.id);
            } else if (result.status === 400) {
              const error = result.data;
              message.error(error);
            } else {
              message.error(result.data);
            }
            handleFetch();
          };

          Modal.confirm({
            title: t('common.confirm'),
            content: 'Koliyə əlavə etmək istədiyinə əminsən?',
            onOk: action,
          });
        }, [original.id]);



        const overlay = (
          <Menu>
            <Menu.Item onClick={onUpdate} icon={<Icons.EditOutlined />}>
              {t('common.actions.update')}
            </Menu.Item>
            <Menu.Item onClick={onDelete} icon={<Icons.DeleteOutlined />}>
              {t('common.actions.delete')}
            </Menu.Item>
            <Menu.Item disabled={!currentParcel || !original.counts.declarations} onClick={addToContainer} icon={<Icons.DropboxOutlined />}>
              Koliyə at
            </Menu.Item>
            <Menu.Item onClick={toggleTransferModal} icon={<Icons.ArrowRightOutlined />}>
              Transfer et
            </Menu.Item>
          </Menu>
        );

        return (
          <StopPropagation>
            <Dropdown overlay={overlay}>
              <Button size='small' icon={<Icons.MoreOutlined />} />
            </Dropdown>
          </StopPropagation>
        );
      },
    }),
    [handleFetch, navigate, t, currentParcel],
  );

  return useMemo<Column<Basket>[]>(
    () => [
      actions,
      {
        ...nextTableColumns.small,
        accessor: (row) => row.id,
        id: 'id',
        Header: t('common.fields.id'),
      },
      {
        accessor: (row) => row.name,
        id: 'basket_name',
        Header: t('common.fields.name'),
      },
      {
        accessor: (row) => `${row.counts.declared}/${row.counts.declarations}`,
        id: 'declared_count',
        filterable: false,
        Header: 'Bəyan edilmiş/Bağlamalar',
      },
      {
        accessor: (row) => row.users,
        id: 'users',
        Header: t('common.fields.assigned'),
        Cell: ({ cell: { value } }) => {
          return value.map((user) => user.name).join(', ');
        },
      },
    ],
    [actions, t],
  );
};
